import React, { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { ItemActions, ModalContext, useL2DTheme } from '@europrocurement/flexy-components';
import {
    UPDATE_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
} from '@europrocurement/l2d-modules/modules/Dossiers/providers';
import { UpdateDossierPayload } from '@europrocurement/l2d-modules/modules/Dossiers/types/UpdateDossierPayload';
import { useDossier } from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import { EditFolioSocieteRefForm } from '../../../shared/EditFolioSocieteRefForm/EditFolioSocieteRefForm';

interface ClientReferenceProps {
    dossierId: number;
    societeRef?: string;
}

export const ClientReference: React.FunctionComponent<ClientReferenceProps> = function ({
    dossierId,
    societeRef,
}) {
    const { modalActions } = React.useContext(ModalContext);
    const [refHovered, setRefHovered] = useState(false);
    const { siblings } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();
    const { updateDossier } = useDossier();
    const { palette } = useL2DTheme();

    const handleCancel = useCallback(() => {
        modalActions.close();
    }, [modalActions]);

    const handleSuccess = useCallback(async () => {
        try {
            setRefHovered(false);
            modalActions.close();
        } catch (error) {
            console.error('Erreur lors de la mise à jour:', error);
        }
    }, [modalActions]);

    const dossier = siblings?.find((dos) => dos.id === dossierId);

    const openEditFolioSocieteRefForm = useCallback(() => {
        if (!dossier) {
            return;
        }

        modalActions.call(
            <EditFolioSocieteRefForm
                onCancel={handleCancel}
                onSuccess={handleSuccess}
                dossier={dossier}
                updateDossier={async (payload: UpdateDossierPayload) => {
                    const updatedDossier = await updateDossier(dossier.id as number, payload);
                    if (updatedDossier) {
                        if (dispatch) {
                            dispatch({
                                type: UPDATE_CURRENT_DOSSIER_CTX,
                                payload: {
                                    dossierId: dossier.id as number,
                                    data: {
                                        ...dossier,
                                        societeRef: payload.societeRef,
                                    },
                                },
                            });
                        }
                    }
                }}
            />,
        );
    }, [dispatch, dossier, handleCancel, handleSuccess, modalActions, updateDossier]);

    const editFolioSocieteRef = useCallback(() => {
        if (!dossier) return;

        openEditFolioSocieteRefForm();
    }, [dossier, openEditFolioSocieteRefForm]);

    const handleDeleteSocieteRef = useCallback(async () => {
        if (!dossier) {
            return;
        }

        try {
            const updatedDossier = await updateDossier(dossier.id as number, { societeRef: '' });
            if (updatedDossier) {
                if (dispatch) {
                    dispatch({
                        type: UPDATE_CURRENT_DOSSIER_CTX,
                        payload: {
                            dossierId: dossier.id as number,
                            data: {
                                ...dossier,
                                societeRef: '',
                            },
                        },
                    });
                }
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour:', error);
        }
    }, [dossier, updateDossier, dispatch]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                position: 'relative',
                padding: '4px 0',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={() => setRefHovered(true)}
            onMouseLeave={() => setRefHovered(false)}
        >
            <Typography
                variant="l2dbodys"
                color={palette.text.primary}
            >
                Référence client:
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {societeRef ? (
                    <Typography
                        variant="l2dbodys"
                        color={palette.grey[700]}
                        sx={{ fontWeight: 500 }}
                    >
                        {societeRef}
                    </Typography>
                ) : (
                    <Typography
                        variant="l2dbodys"
                        color={palette.text.secondary}
                        sx={{ fontStyle: 'italic' }}
                    >
                        Non renseignée
                    </Typography>
                )}

                <ItemActions
                    onEdit={editFolioSocieteRef}
                    onDelete={societeRef ? handleDeleteSocieteRef : undefined}
                    editTooltip="Éditer la référence client"
                    deleteTooltip="Supprimer la référence client"
                    copyValue={societeRef}
                    visible={refHovered}
                    slideDirection="left"
                />
            </Box>
        </Box>
    );
};
